import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { isAndroidPlatform, useToast } from "@src/appV2/lib";
import { writeToClipboard } from "@src/appV2/NativeAdapters/clipboard.adapter";
import { BottomSheet, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useCallback } from "react";

import { BottomSheetHeader } from "../components/BottomSheetHeader";
import { Button } from "../components/Button";
import { DialogFooter } from "../components/DialogFooter";
import { useOpenGeoLocationContext } from "./useOpenGeoLocation";

interface OpenGeoLocationBottomSheetProps {
  modalState?: UseModalState;
}

/**
 * This component is used to open the map in the bottom sheet.
 * This works with modalState passed as a prop (for route based modals)
 * or the context modal state as a fallback.
 *
 * Ideally we should move all our usages to only used route-based modals
 */
export function OpenGeoLocationBottomSheet(props: OpenGeoLocationBottomSheetProps = {}) {
  const { modalState: overrideModalState } = props;

  const {
    modalState: contextModalState,
    appleMapsHref,
    googleMapsHref,
    formattedAddress,
  } = useOpenGeoLocationContext();

  const modalState = overrideModalState ?? contextModalState;

  const { showSuccessToast } = useToast();

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      footer={<DialogFooter orientation="vertical" />}
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
    >
      <DialogContent sx={{ padding: 5 }}>
        <Stack spacing={4}>
          <Button
            variant="outlined"
            href={googleMapsHref}
            size="large"
            onClick={(event) => {
              event.stopPropagation();
              modalState.closeModal();
            }}
          >
            Open in Google Maps
          </Button>
          {!isAndroidPlatform() && (
            <Button
              variant="outlined"
              href={appleMapsHref}
              size="large"
              onClick={(event) => {
                event.stopPropagation();
                modalState.closeModal();
              }}
            >
              Open in Apple Maps
            </Button>
          )}
          {isDefined(formattedAddress) && (
            <Button
              variant="outlined"
              size="large"
              onClick={async (event) => {
                event.stopPropagation();
                await writeToClipboard(formattedAddress);
                showSuccessToast("Successfully copied address!");
                modalState.closeModal();
              }}
            >
              Copy Address
            </Button>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
